<template>
    <div v-if="isTopbarVisible" class="layout-topbar">
        <div class="tw:flex tw:sm:gap-[2rem]">
            <div v-if="user && hasSideNav" class="tw:flex tw:justify-center tw:sm:w-[6rem]">
                <button
                    ref="menubutton"
                    class="topbar-menubutton tw:bg-transparent tw:border-none tw:text-[var(--text-color)]"
                    type="button"
                    @click="handleMenuButtonClick"
                >
                    <span />
                </button>
            </div>

            <router-link
                :to="{ name: 'home' }"
                class="edverum-logo-link tw:w-[40px] tw:overflow-hidden tw:sm:w-auto tw:sm:overflow-visible"
                aria-label="Home"
            >
                <edverum-logo id="application_logo" class="is-logo" />
            </router-link>
        </div>

        <ul class="topbar-menu">
            <div class="tw:hidden tw:xl:flex tw:items-center tw:gap-x-4">
                <router-link
                    v-if="user"
                    id="application_navigation_home"
                    class="tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
                    to="/"
                >
                    <e-button
                        type="secondary"
                        class="tw:font-semibold tw:bg-transparent tw:border-none"
                    >
                        Dashboard
                    </e-button>
                </router-link>

                <div
                    v-if="canSeeCourseDropdown"
                    class="dropdown-wrapper tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
                    @mouseenter="showCoursesDropdown"
                    @mouseleave="hideCoursesDropdown"
                    @focusin="showCoursesDropdown"
                    @focusout="hideCoursesDropdown"
                >
                    <e-dropdown ref="coursesDropdown">
                        <template #trigger>
                            <e-button
                                icon-right="angle-down"
                                type="secondary"
                                class="tw:font-semibold tw:bg-transparent tw:border-none"
                                :style="isCoursesActive ? { color: 'var(--p-primary-color)' } : {}"
                            >
                                Courses
                            </e-button>
                        </template>
                        <Menu
                            :model="coursesMenuItems"
                            @mouseenter="showCoursesDropdown"
                            @mouseleave="hideCoursesDropdown"
                            @focusin="showCoursesDropdown"
                            @focusout="hideCoursesDropdown"
                        >
                            <template #item="{ item, props: itemProps }">
                                <router-link
                                    v-if="item.route && item.allowed && !item.header"
                                    v-slot="{ href, navigate }"
                                    :to="item.route"
                                    custom
                                >
                                    <a
                                        v-bind="itemProps.action"
                                        class="tw:text-[var(--text-color)]! tw:font-medium!"
                                        :class="{ 'tw:text-sm!': item.small }"
                                        :href="href"
                                        @click="event => navigateAndCloseSidebar(event, navigate, hideCoursesDropdown)"
                                    >
                                        <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                                    </a>
                                </router-link>
                                <div v-else-if="item.header" class="p-menuitem-header tw:text-base!">
                                    {{ item.label }}
                                </div>
                            </template>
                        </Menu>
                    </e-dropdown>
                </div>

                <div
                    v-if="canSeeAdministrationDropdown"
                    class="dropdown-wrapper tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
                    @mouseenter="showAdministrationDropdown"
                    @mouseleave="hideAdministrationDropdown"
                    @focusin="showAdministrationDropdown"
                    @focusout="hideAdministrationDropdown"
                >
                    <e-dropdown ref="administrationDropdown">
                        <template #trigger>
                            <e-button
                                icon-right="angle-down"
                                type="secondary"
                                class="tw:font-semibold tw:bg-transparent tw:border-none"
                                :style="isAdministrationActive ? { color: 'var(--p-primary-color)' } : {}"
                            >
                                Administration
                            </e-button>
                        </template>
                        <Menu
                            :model="administrationMenuItems"
                            @mouseenter="showAdministrationDropdown"
                            @mouseleave="hideAdministrationDropdown"
                            @focusin="showAdministrationDropdown"
                            @focusout="hideAdministrationDropdown"
                        >
                            <template #item="{ item, props: itemProps }">
                                <router-link
                                    v-if="item.route && item.allowed"
                                    v-slot="{ href, navigate }"
                                    :to="item.route"
                                    custom
                                >
                                    <a
                                        v-bind="itemProps.action"
                                        :href="href"
                                        class="tw:text-[var(--text-color)]! tw:font-medium!"
                                        @click="
                                            event =>
                                                navigateAndCloseSidebar(event, navigate, hideAdministrationDropdown)
                                        "
                                    >
                                        <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                                    </a>
                                </router-link>
                                <a
                                    v-else-if="item.url && item.allowed"
                                    v-bind="itemProps.action"
                                    :href="item.url"
                                    :target="item.target"
                                >
                                    <span class="ml-2 tw:text-[var(--text-color)] tw:text-base!">{{ item.label }}</span>
                                </a>
                            </template>
                        </Menu>
                    </e-dropdown>
                </div>

                <div
                    v-if="user"
                    class="dropdown-wrapper tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
                    @mouseenter="showResourcesDropdown"
                    @mouseleave="hideResourcesDropdown"
                    @focusin="showResourcesDropdown"
                    @focusout="hideResourcesDropdown"
                >
                    <e-dropdown id="application_navigation_resources" ref="resourcesDropdown">
                        <template #trigger>
                            <e-button
                                icon-right="angle-down"
                                type="secondary"
                                class="tw:font-semibold tw:bg-transparent tw:border-none"
                                :style="isResourcesActive ? { color: 'var(--p-primary-color)' } : {}"
                            >
                                Resources
                            </e-button>
                        </template>
                        <Menu
                            :model="resourcesMenuItems"
                            @mouseenter="showResourcesDropdown"
                            @mouseleave="hideResourcesDropdown"
                            @focusin="showResourcesDropdown"
                            @focusout="hideResourcesDropdown"
                        >
                            <template #item="{ item, props: itemProps }">
                                <router-link
                                    v-if="item.route && item.allowed"
                                    v-slot="{ href, navigate }"
                                    :to="item.route"
                                    custom
                                >
                                    <a
                                        v-bind="itemProps.action"
                                        :href="href"
                                        class="tw:text-[var(--text-color)]! tw:font-medium! tw:text-base!"
                                        @click="
                                            event => navigateAndCloseSidebar(event, navigate, hideResourcesDropdown)
                                        "
                                    >
                                        <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                                    </a>
                                </router-link>
                            </template>
                        </Menu>
                    </e-dropdown>
                </div>
            </div>
        </ul>

        <div class="topbar-actions">
            <global-search
                v-if="user && initialState('GlobalSearchEnabled', false)"
                class="tw:flex tw:items-center"
            />
            <div v-if="user && !shouldHideNavigation">
                <alert-notification
                    v-if="$gate.allows('viewAny', 'Message') && showNotificationBell"
                    class="navbar-item p-none mobile"
                />
            </div>
            <div class="topbar-profile">
                <div
                    v-if="user"
                    class="tw:relative tw:flex tw:items-center tw:gap-2"
                    @click="toggleUserMenu"
                    @mouseenter="showUserMenu"
                    @mouseleave="hideUserMenu"
                    @focusin="showUserMenu"
                    @focusout="hideUserMenu"
                >
                    <button class="layout-topbar-action">
                        <user-avatar
                            :title="user?.name"
                            :img-url="user?.photo_url"
                            size="32x32"
                            :initials="user?.initials"
                            use-initials-when-no-image
                        />
                    </button>
                    <p class="tw:font-semibold tw:hidden tw:lg:block">{{ user?.name }}</p>
                    <i class="uil uil-angle-down tw:text-base" />
                    <Menu
                        v-if="userMenuVisible"
                        :model="userMenuItems"
                        class="tw:absolute tw:right-0 tw:top-full tw:mt-2 tw:min-w-48"
                        :pt="{
                            root: { class: 'tw:shadow-lg tw:rounded-border' },
                        }"
                    >
                        <template #item="{ item, props: itemProps }">
                            <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
                                <a
                                    v-bind="itemProps.action"
                                    :href="href"
                                    style="color: inherit; text-decoration: none; font-weight: 500"
                                    @click="event => navigateAndCloseSidebar(event, navigate, hideUserMenu)"
                                >
                                    <i v-if="item.icon" :class="item.icon" />
                                    <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                                </a>
                            </router-link>
                            <a
                                v-else
                                v-bind="itemProps.action"
                                style="color: inherit; text-decoration: none; font-weight: 500"
                            >
                                <i v-if="item.icon" :class="item.icon" />
                                <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                            </a>
                        </template>
                    </Menu>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import intersection from 'lodash/intersection.js';
import some from 'lodash/some.js';
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AlertNotification from '@/components/alerts/AlertNotification.vue';
import GlobalSearch from '@/components/app/GlobalSearch.vue';
import UserAvatar from '@/components/elements/Avatar.vue';
import EdverumLogo from '@/components/elements/EdverumLogo.vue';
import { initialState } from '@/Helpers.js';
import Page from '@/models/pages/Page.js';
import User from '@/models/User.js';
import UserSetting from '@/models/UserSetting.js';
import axios from '@/plugins/axios.js';
import Gate from '@/plugins/vue-gate/Gate.js';
import { gate } from '@/plugins/vue-gate/Index.js';
import Utilities from '@/utilities/Utilities.js';
import { useLayout } from './composables/layout.js';

const {
    toggleMenu,
    closeMenu,
    toggleSidebarSummary,
    tabs,
    closeTab,
    isDarkTheme,
    toggleConfigSidebar,
    isTopbarVisible,
} = useLayout();
const { t: translate } = useI18n();
const router = useRouter();
const store = useStore();
const instance = getCurrentInstance();
const { $hasFeature } = instance.appContext.config.globalProperties;

const props = defineProps({
    shouldHideNavigation: Boolean,
    hasSideNav: Boolean,
});

const user = computed(() => {
    return User.query().with(Gate.userRelationships()).find(store.state.Auth.user?.id);
});

// TODO: When search is supported, re-enable this
// const searchActive = ref(false);
// function activateSearch() {
//     searchActive.value = true;
//
//     setTimeout(() => {
//         const input = document.querySelector('.searchInput');
//         input.focus();
//     }, 100);
// }
//
// function deactivateSearch() {
//     searchActive.value = false;
// }

function onCloseTab(index) {
    if (tabs.value.length > 1) {
        if (index === tabs.value.length - 1) router.push(tabs.value[tabs.value.length - 2].to);
        else router.push(tabs.value[index + 1].to);
    } else {
        router.push('/');
    }
    closeTab(index);
}

// Data properties
const active = ref(false);
const userActive = ref(false);
const enrolledCourses = ref([]);
const userMenuVisible = ref(false);
const userMenuHideTimeout = ref(null);

// Dropdown refs and timeouts
const coursesDropdown = ref(null);
const coursesHideTimeout = ref(null);
const administrationDropdown = ref(null);
const administrationHideTimeout = ref(null);
const resourcesDropdown = ref(null);
const resourcesHideTimeout = ref(null);

// Computed properties

const canSeeCourseDropdown = computed(() => {
    if (!user.value) return false;

    return (
        gate.allows('viewAny', 'Course') ||
        gate.allows('viewAny', 'CourseTemplate') ||
        user.value.isStudentSupport() ||
        gate.allows('viewAny', 'Meeting') ||
        enrolledCourses.value.length > 0
    );
});

const canSeeAdministrationDropdown = computed(() => {
    if (!user.value) return false;

    return some([
        gate.allows('viewAny', 'Video'),
        gate.allows('viewAny', 'AttemptAuthorization'),
        gate.allows('viewAny', 'RubricBank'),
        gate.allows('viewAny', 'Remediation'),
        gate.allows('viewAny', 'SlideDeck'),
        gate.allows('viewAny', 'User'),
        gate.allows('viewAny', 'Integrations'),
        gate.allows('viewAny', 'Quiz'),
        gate.allows('viewAny', 'Page'),
        gate.allows('viewAny', 'SlideDeck'),
        gate.allows('viewAny', 'RubricBank'),
        gate.allows('viewAny', 'Group'),
        gate.allows('viewAny', 'Location'),
        gate.allows('_viewRiskAssessment', 'Report'),
        gate.allows('viewAny', 'Report'),
        gate.userCan('view the activity log'),
        gate.userCan('view pulse'),
        gate.userCan('view websockets'),
    ]);
});

const isInDevMode = computed(() => {
    return import.meta.env.MODE === 'development' || window.location.host === 'edverum.test';
});

const showHelpNavLink = computed(() => {
    if (store.getters['InstitutionSettings/get']('support_documentation_enabled')) {
        return true;
    }

    return gate.userCan('view all help articles');
});

const pageAuthorizedRoleIds = computed(() => {
    return Page.getPageAuthorizedRoleIds();
});

const showNotificationBell = computed(() => {
    if (store.getters['InstitutionSettings/get']('notifications_enabled')) {
        return true;
    }

    return !!(store.getters['User/hasRole']('Admin') || store.getters['User/hasRole']('Owner'));
});

const showEditPageNav = computed(() => {
    if (!user.value) return false;

    if (store.getters['User/hasRole']('Admin') || store.getters['User/hasRole']('Owner')) return true;

    if (!store.getters['InstitutionSettings/get']('page_enabled')) return false;

    const userGroupRoleIds = user.value.user_groups.map(group => group.role_id);

    return intersection(userGroupRoleIds, pageAuthorizedRoleIds.value).length > 0 || gate.allows('viewAny', 'Page');
});

const coursesMenuItems = computed(() => {
    const topLevelItems = [
        {
            label: 'Course Templates',
            route: { name: 'course-templates' },
            allowed: gate.allows('viewAny', 'CourseTemplate'),
        },
        {
            label: 'All Courses',
            route: { name: 'courses' },
            allowed: gate.allows('viewAny', 'Course'),
        },
        {
            label: 'Course Codes',
            route: { name: 'course-codes' },
            allowed: gate.allows('viewAny', 'CourseCode'),
        },
        {
            label: 'Meetings',
            route: { name: 'meetings-index' },
            allowed: gate.allows('viewAny', 'Meeting'),
        },
        {
            label: 'Due Dates and Schedules',
            route: { name: 'activities-view-upcoming' },
            allowed: gate.allows('view', 'Activity') && gate.allows('viewAny', 'TimePeriod'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    const items = [...topLevelItems];

    // Add separator and enrolled courses section if they exist
    if (enrolledCourses.value.length > 0) {
        if (items.some(item => item.allowed)) {
            items.push({ separator: true });
        }
        items.push({
            label: 'Current Courses',
            route: { name: 'courses' },
            allowed: gate.allows('viewAny', 'Course'),
            small: true,
        });

        // Add enrolled courses sorted alphabetically by title
        const sortedEnrolledCourses = [...enrolledCourses.value].sort((a, b) =>
            (a.course?.title || '').localeCompare(b.course?.title || '')
        );
        sortedEnrolledCourses.forEach(({ course, statuses }) => {
            items.push({
                label: course.title,
                route: { name: 'course-dashboard', params: { courseId: course.id } },
                allowed: true,
                small: true,
            });
        });
    }

    // Filter to only include allowed items
    return items.filter(item => item.separator || item.header || item.allowed);
});

const inboxEnabled = computed(() => {
    return $hasFeature('INBOX');
});

const resourcesMenuItems = computed(() => {
    return [
        {
            label: 'Help & Support',
            route: { name: 'help-home' },
            allowed: showHelpNavLink.value,
        },
        {
            label: 'Inbox',
            route: { name: 'inbox' },
            allowed: inboxEnabled.value,
        },
        {
            label: 'My Notes',
            route: { name: 'notes-home' },
            allowed: true,
        },
    ].sort((a, b) => a.label.localeCompare(b.label));
});

const administrationMenuItems = computed(() => {
    const mainSectionItems = [
        {
            label: 'Activity Log',
            route: { name: 'activity-log-index' },
            allowed: gate.userCan('view the activity log'),
        },
        {
            label: 'Attempt Authorizations',
            route: { name: 'attempt-authorizations-index' },
            allowed: gate.allows('viewAny', 'AttemptAuthorization'),
        },
        {
            label: 'Dashboards',
            route: { name: 'dashboards' },
            allowed: true,
        },
        {
            label: 'Evaluation Templates',
            route: { name: 'evaluation-templates-index' },
            allowed: gate.allows('viewAny', 'EvaluationTemplate'),
        },
        {
            label: 'Groups',
            route: { name: 'groups-index' },
            allowed: gate.allows('viewAny', 'Group'),
        },
        {
            label: 'Ebooks',
            route: { name: 'ebooks-index' },
            allowed: gate.allows('viewAny', 'Ebook'),
        },
        {
            label: 'Integrations',
            route: { name: 'integrations-index' },
            allowed: gate.allows('viewAny', 'Integrations'),
        },
        {
            label: 'Locations',
            route: { name: 'locations-index' },
            allowed: gate.allows('viewAny', 'Location'),
        },
        {
            label: 'Pages',
            route: { name: 'edit-pages-index' },
            allowed: showEditPageNav.value,
        },
        {
            label: 'People',
            route: { name: 'people-index' },
            allowed: gate.allows('viewAny', 'User'),
        },
        {
            label: 'Institution Settings',
            route: { name: 'institution-settings-profile' },
            allowed: gate.userCan('view an institution setting'),
        },
        {
            label: 'Proctor Dashboard',
            url: '/proctor-dashboard',
            target: '_blank',
            allowed: true,
        },
        {
            label: 'Remediations',
            route: { name: 'remediations-index' },
            allowed: gate.allows('viewAny', 'Remediation'),
        },
        {
            label: 'Reports',
            route: { name: 'reports-index' },
            allowed: gate.allows('viewAny', 'Report'),
        },
        {
            label: 'Risk Assessment Report',
            route: { name: 'risk-assessment-report' },
            allowed: gate.userCan('view risk assessment report') && !gate.allows('viewAny', 'Report'),
        },
        {
            label: 'Rubric Templates',
            route: { name: 'rubric-bank-index' },
            allowed: gate.allows('viewAny', 'RubricBank'),
        },
        {
            label: 'Slide Decks',
            route: { name: 'slide-decks-index' },
            allowed: gate.allows('viewAny', 'SlideDeck'),
        },
        {
            label: 'Surveys',
            route: { name: 'surveys-index' },
            allowed: gate.allows('viewAny', 'Quiz'),
        },
        {
            label: 'Videos',
            route: { name: 'videos-index' },
            allowed: gate.allows('viewAny', 'Video'),
        },
        {
            label: 'Quizzes',
            route: { name: 'quizzes-index' },
            allowed: gate.allows('viewAny', 'Quiz'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    const superAdminSectionItems = [
        {
            label: 'Horizon',
            url: '/horizon',
            target: '_blank',
            allowed: gate.userCan('view horizon'),
        },
        {
            label: 'Pulse',
            url: '/pulse',
            target: '_blank',
            allowed: gate.userCan('view pulse'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    return [...mainSectionItems, { separator: true }, ...superAdminSectionItems];
});

const selected = computed(() => {
    return Number(store.getters['User/activeDarkTheme']());
});

const isCoursesActive = computed(() => {
    const currentRouteName = router.currentRoute.value.name;
    return coursesMenuItems.value.some(item => item.route && item.route.name === currentRouteName);
});

const isAdministrationActive = computed(() => {
    const currentRouteName = router.currentRoute.value.name;
    return administrationMenuItems.value.some(item => item.route && item.route.name === currentRouteName);
});

const isResourcesActive = computed(() => {
    const currentRouteName = router.currentRoute.value.name;
    return resourcesMenuItems.value.some(item => item.route && item.route.name === currentRouteName);
});

const shouldUseDirectSSOLogin = computed(() => {
    if (isInDevMode.value) {
        return false;
    }

    return initialState('RedirectToSingleSignOn');
});

function logout() {
    const selectApp = document.getElementsByTagName('html')[0];
    selectApp.classList.remove('dark-mode');
    selectApp.classList.remove('invert-black');
    localStorage.removeItem('darkThemePreference');

    window.location.href = '/logout';
}

function navigateAndCloseSidebar($event, navigate, hideDropdown) {
    hideDropdown();
    if (window.innerWidth < 1200) {
        closeMenu();
    }
    navigate($event);
}

const darkModeSetting = computed(() => {
    return UserSetting.findByKey('dark_mode');
});

async function toggleDarkMode() {
    await darkModeSetting.value.toggle();
}

const userMenuItems = computed(() => {
    return [
        {
            label: 'Profile',
            icon: 'uil uil-user',
            route: { name: 'me' },
        },
        {
            label: 'Light/Dark Mode',
            icon: isDarkTheme.value ? 'uil uil-moon' : 'uil uil-sun',
            command: () => toggleDarkMode(),
        },
        {
            label: 'Theme Switcher',
            icon: 'uil uil-palette',
            command: () => toggleConfigSidebar(),
        },
        {
            label: translate('logout'),
            icon: 'uil uil-sign-out-alt',
            command: () => logout(),
        },
    ];
});

// Methods
async function loadEnrollments() {
    const preloaded = Utilities.initialState('UserEnrollments');

    if (preloaded?.data?.length || import.meta.env.VITE_APP_DEBUG === 'true') {
        enrolledCourses.value = preloaded.data.filter(enrollment => enrollment.course?.is_accessible);
    }

    if (!enrolledCourses.value.length || import.meta.env.VITE_APP_DEBUG === 'true') {
        const { data } = await axios.get('/api/user-enrollments');

        enrolledCourses.value = data.data.filter(enrollment => enrollment.course?.is_accessible);
    }
}

function toggleNavBar() {
    active.value = !active.value;
}

function toggleUserNavBar() {
    userActive.value = !userActive.value;
}

const edverumNavBar = ref(null);
const edverumUserNavBar = ref(null);

function documentClick(e) {
    const el = edverumNavBar.value;
    const userEl = edverumUserNavBar.value;

    const { target } = e;

    if (el && el !== target && !el.contains(target)) {
        active.value = false;
    }

    if (userEl && userEl !== target && !userEl.contains(target)) {
        userActive.value = false;
    }
}

async function setDarkPreference(theme) {
    await store.dispatch('User/setDarkPreferredTheme', theme);
}

function showUserMenu() {
    // Clear any pending hide timeout
    if (userMenuHideTimeout.value) {
        clearTimeout(userMenuHideTimeout.value);
        userMenuHideTimeout.value = null;
    }
    userMenuVisible.value = true;
}

function hideUserMenu() {
    // Add a small delay before hiding to allow user to reach the menu
    userMenuHideTimeout.value = setTimeout(() => {
        userMenuVisible.value = false;
        userMenuHideTimeout.value = null;
    }, 100); // 200ms delay
}

function toggleUserMenu() {
    userMenuVisible.value = !userMenuVisible.value;
}

function handleMenuButtonClick() {
    // On mobile, toggle the sidebar visibility
    if (window.innerWidth < 1200) {
        toggleMenu();
    } else {
        // On desktop, toggle between summary and menu views
        toggleSidebarSummary();
    }
}

// Dropdown hover handlers
function showCoursesDropdown() {
    if (coursesHideTimeout.value) {
        clearTimeout(coursesHideTimeout.value);
        coursesHideTimeout.value = null;
    }
    coursesDropdown.value?.show();
}

function hideCoursesDropdown() {
    coursesHideTimeout.value = setTimeout(() => {
        coursesDropdown.value?.hide();
        coursesHideTimeout.value = null;
    }, 100);
}

function showAdministrationDropdown() {
    if (administrationHideTimeout.value) {
        clearTimeout(administrationHideTimeout.value);
        administrationHideTimeout.value = null;
    }
    administrationDropdown.value?.show();
}

function hideAdministrationDropdown() {
    administrationHideTimeout.value = setTimeout(() => {
        administrationDropdown.value?.hide();
        administrationHideTimeout.value = null;
    }, 100);
}

function showResourcesDropdown() {
    if (resourcesHideTimeout.value) {
        clearTimeout(resourcesHideTimeout.value);
        resourcesHideTimeout.value = null;
    }
    resourcesDropdown.value?.show();
}

function hideResourcesDropdown() {
    resourcesHideTimeout.value = setTimeout(() => {
        resourcesDropdown.value?.hide();
        resourcesHideTimeout.value = null;
    }, 100);
}

// Watchers
watch(
    user,
    (newUser, oldUser) => {
        if (newUser && (!oldUser || oldUser.id !== newUser.id)) {
            loadEnrollments();
        }
    },
    { immediate: true }
);

// Lifecycle
onMounted(() => {
    document.addEventListener('click', documentClick);
});

onUnmounted(() => {
    document.removeEventListener('click', documentClick);
    // Clean up any pending timeouts
    if (userMenuHideTimeout.value) {
        clearTimeout(userMenuHideTimeout.value);
    }
    if (coursesHideTimeout.value) {
        clearTimeout(coursesHideTimeout.value);
    }
    if (administrationHideTimeout.value) {
        clearTimeout(administrationHideTimeout.value);
    }
    if (resourcesHideTimeout.value) {
        clearTimeout(resourcesHideTimeout.value);
    }
});
</script>

<style scoped lang="scss">
@import '~assets/scss/toolbox.scss';

.edverum-logo-link {
    &:hover {
        background-color: var(--surface-hover);
        border-radius: 0.5rem;
    }
}
</style>
